import { API_CONFIG } from "../../../utils/apiConfig";
import { AdminLoginRequest, AdminRegisterRequest, AdminLoginResponse } from "../interfaces/adminAuth.interface";

const ADMIN_API_BASE = API_CONFIG.ADMIN_API_BASE_URL;

class AdminAuthService {
  private static instance: AdminAuthService;

  static getInstance(): AdminAuthService {
    if (!AdminAuthService.instance) {
      AdminAuthService.instance = new AdminAuthService();
    }
    return AdminAuthService.instance;
  }

  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const token = localStorage.getItem(API_CONFIG.ADMIN_TOKEN_STORAGE_KEY);
    const config: RequestInit = {
      headers: {
        "Content-Type": "application/json",
        ...(token && { Authorization: `Bearer ${token}` }),
        ...options.headers,
      },
      ...options,
    };
    const response = await fetch(`${ADMIN_API_BASE}${endpoint}`, config);
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || error.message || `HTTP error! status: ${response.status}`);
    }
    return response.json();
  }

  login = async (credentials: AdminLoginRequest) => {
    return this.request<{ success: boolean; data: AdminLoginResponse }>("/auth/login", {
      method: "POST",
      body: JSON.stringify(credentials),
    });
  };

  register = async (data: AdminRegisterRequest) => {
    return this.request<{ success: boolean; data: { adminId: string; message: string } }>("/auth/register", {
      method: "POST",
      body: JSON.stringify(data),
    });
  };

  refreshToken = async (refreshToken: string) => {
    return this.request<{ success: boolean; data: { accessToken: string; refreshToken: string } }>("/auth/refresh", {
      method: "POST",
      body: JSON.stringify({ refreshToken }),
    });
  };

  logout = async () => {
    const refreshToken = localStorage.getItem(API_CONFIG.ADMIN_REFRESH_TOKEN_KEY);
    return this.request<{ success: boolean }>("/auth/logout", {
      method: "POST",
      body: JSON.stringify({ refreshToken }),
    });
  };

  getProfile = async () => {
    return this.request<{ success: boolean; data: { admin: any } }>("/auth/me");
  };

  verifyEmail = async (email: string, verificationCode: string) => {
    return this.request<{ success: boolean; message: string }>("/auth/verify-email", {
      method: "POST",
      body: JSON.stringify({ email, verificationCode }),
    });
  };

  resendVerification = async (email: string) => {
    return this.request<{ success: boolean; message: string }>("/auth/resend-verification", {
      method: "POST",
      body: JSON.stringify({ email }),
    });
  };

  verifyOTP = async (email: string, code: string) => {
    return this.request<{ success: boolean; data: { user: any } }>("/auth/verify-otp", {
      method: "POST",
      body: JSON.stringify({ email, code }),
    });
  };

  forgotPassword = async (email: string) => {
    return this.request<{ success: boolean; message: string }>("/auth/forgot-password", {
      method: "POST",
      body: JSON.stringify({ email }),
    });
  };

  updateProfile = async (data: Record<string, unknown>) => {
    return this.request<{ success: boolean; data: { admin: any } }>("/auth/profile", {
      method: "PUT",
      body: JSON.stringify(data),
    });
  };

  uploadProfileImage = async (file: File) => {
    const token = localStorage.getItem(API_CONFIG.ADMIN_TOKEN_STORAGE_KEY);
    const formData = new FormData();
    formData.append("profileImage", file);
    const response = await fetch(`${ADMIN_API_BASE}/auth/profile/image`, {
      method: "POST",
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: formData,
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.message || `HTTP error! status: ${response.status}`);
    }
    return response.json();
  };

  changePassword = async (data: { currentPassword: string; newPassword: string; confirmPassword: string }) => {
    return this.request<{ success: boolean; message: string }>("/auth/change-password", {
      method: "POST",
      body: JSON.stringify(data),
    });
  };

  enable2FA = async (data: { method: string; phoneNumber?: string; phoneCode?: string }) => {
    return this.request<{ success: boolean; data?: any }>("/auth/2fa/enable", {
      method: "POST",
      body: JSON.stringify(data),
    });
  };

  disable2FA = async (data: { email: string; password: string }) => {
    return this.request<{ success: boolean; message: string }>("/auth/2fa/disable", {
      method: "POST",
      body: JSON.stringify(data),
    });
  };

  get2FAStatus = async () => {
    return this.request<{ success: boolean; data: { isEnabled: boolean; method?: string } }>("/auth/2fa/status");
  };

  verify2FACode = async (code: string) => {
    return this.request<{ success: boolean; message: string }>("/auth/2fa/verify", {
      method: "POST",
      body: JSON.stringify({ code }),
    });
  };

  resend2FACode = async () => {
    return this.request<{ success: boolean; message: string }>("/auth/2fa/resend", {
      method: "POST",
    });
  };

  getSessions = async (params: { page: number; limit: number }) => {
    const query = new URLSearchParams({ page: String(params.page), limit: String(params.limit) }).toString();
    return this.request<{ success: boolean; data: { sessions: any[]; total: number } }>(`/auth/sessions?${query}`);
  };

  revokeSession = async (sessionId: string) => {
    return this.request<{ success: boolean; message: string }>(`/auth/sessions/${sessionId}`, {
      method: "DELETE",
    });
  };

  revokeAllOtherSessions = async () => {
    return this.request<{ success: boolean; message: string }>("/auth/sessions", {
      method: "DELETE",
    });
  };

  verifyCurrentPassword = async (password: string) => {
    return this.request<{ success: boolean; message: string }>("/auth/verify-password", {
      method: "POST",
      body: JSON.stringify({ password }),
    });
  };
}

export const adminAuthService = AdminAuthService.getInstance();
